import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Link } from '@mui/icons-material';
import { FormTextField } from 'shared/_ui/form/form-text-field';
import { SHARE_TYPES } from 'shared/constants/share';
import { SHARE_LINK_SCHEMA, ShareAccessType } from 'shared/_model/sharing/ShareDialog/constants'
import {
	AccessTypeButton,
	AccessTypeButtonGroup,
	ShareDialogForm,
} from 'shared/_model/sharing/ShareDialog/styles';
import AccessTypeButtonContent from 'shared/_model/sharing/ShareDialog/access-type-button-content';
import { IconCopy } from 'shared/_ui/icon';
import { generatePassword, getPasswordStrength } from 'shared/_model/sharing/ShareDialog/utils';
import copy from 'copy-to-clipboard';
import SwitchField from 'shared/_ui/form/switch-field';
import { FormikConfig } from 'formik/dist/types';
import {
	apiShareDelSettings,
	apiShareGetSettings,
	apiShareSaveSettings,
} from 'shared/modules/share/api'
import { frontEndLocation } from 'shared/constants/system'
import { ROUTE } from 'shared/constants/routes_old'
import { updateSharesDoc } from 'shared/utils/share'
import { useDispatch } from 'react-redux';

type ShareDialogProps = {
	documentId: string;
	type: keyof typeof SHARE_TYPES;
	handleClose: () => void;
	onChange?: () => void;
}

const TITLE_MAP: Record<ShareDialogProps['type'], string> = {
	[SHARE_TYPES.project]: 'project',
	[SHARE_TYPES.file]: 'file',
};

type ShareDialogValues = {
	accessType: ShareAccessType,
	password: string,
	documentId: string,
	canAddFiles?: boolean,
	canEdit?: boolean,
	canRemoveFiles?: boolean,
};

const INITIAL_VALUES = {
	accessType: ShareAccessType.password,
	password: '',
	documentId: '',
	enabled: false,
	canAddFiles: false,
	canEdit: false,
	canRemoveFiles: false,
};

export default function ShareDialog({ documentId, handleClose, type, onChange }: ShareDialogProps) {
	const [loaded, setLoaded] = useState(false);
	const formikRef = useRef<any>({} as any);

	const [id, setId] = useState<string>('');
	const [enabled, setEnabled] = useState<boolean>(false);

	const readSettings = useCallback(async () => {
		try {
			const {
				id: idResponse,
				enabled: enabledResponse,
				pass,
				userId,
				...rest
			} = await apiShareGetSettings(documentId);
			await formikRef.current.setValues((s: ShareDialogValues) => ({ ...rest, password: s.password } as any), false);
			setEnabled(enabledResponse);
			setId(idResponse);
		} catch (e) {
			formikRef.current.setValues(INITIAL_VALUES);
		}
	}, [documentId]);

	const dispatch = useDispatch();
	const updateUserDocs = useCallback(async () => {
		await dispatch(updateSharesDoc(documentId, type, id));
	}, [dispatch, documentId, type, id]);

	const handleSubmit = useCallback<FormikConfig<ShareDialogValues>['onSubmit']>(async (values: ShareDialogValues, { setSubmitting, validateForm }) => {
		const errors = await validateForm();
		if (Object.keys(errors).length > 0) {
			return;
		}

		setSubmitting(true);
		const data = {
			...values,
			documentId,
			type,
		};

		try {
			const { sharingId } = await apiShareSaveSettings(data);
			setId(sharingId);

			await readSettings();
			await updateUserDocs();
			await onChange?.();
			await handleClose();
		} catch (e) {
			console.log('Error saving share settings', e);
		} finally {
			setSubmitting(false);
		}
	}, [documentId, type, readSettings, updateUserDocs, onChange]);

	const handleDisableShare = useCallback(async () => {
		formikRef.current.setSubmitting(true);

		try {
			await apiShareDelSettings(id);
			setId('');
			setEnabled(false);
			await readSettings();
			await updateUserDocs();

			await onChange?.();
			await handleClose();
		} finally {
			formikRef.current.setSubmitting(false);
		}
	}, [id, readSettings, updateUserDocs, onChange]);

	const formik = useFormik<ShareDialogValues>({
		initialValues: INITIAL_VALUES,
		onSubmit: handleSubmit,
		validationSchema: SHARE_LINK_SCHEMA,
	});

	const handleAccessTypeChange = useCallback(
		(_: unknown, newAccessType: string | null) => {
			if (newAccessType) {
				formik.setFieldValue('accessType', newAccessType);
			}
		},
		[formik],
	);

	const { accessType } = formik.values;

	// const strength = getPasswordStrength(formik.values.password);
	const shareLink = id ? `${frontEndLocation}${ROUTE.share.id(id)}` : '';

	useEffect(() => {
		readSettings().finally(() => setLoaded(true));
		formikRef.current = formik;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const disabled = formik.isSubmitting || !loaded;

	return (
		<Dialog open onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle variant="h2">
				Share {TITLE_MAP[type]}
			</DialogTitle>
			<DialogContent>
				<ShareDialogForm onSubmit={formik.handleSubmit}>
					<div>
						<Typography fontWeight="bold" mb={1}>
							<Link /> Share Link
						</Typography>
						<Box display="flex" gap={2}>
							<TextField value={shareLink} InputProps={{ readOnly: true }} fullWidth size="small" disabled={disabled}/>
							<Button
								startIcon={<IconCopy />}
								variant="outlined"
								sx={{ textWrapMode: 'nowrap', height: 'auto' }}
								onClick={() => {
									copy(shareLink);
								}}
								disabled={disabled || !id}
							>
								Copy
							</Button>
						</Box>
					</div>
					<Box display="flex" flexDirection="column">
						<AccessTypeButtonGroup disabled={disabled} value={accessType} exclusive onChange={handleAccessTypeChange}>
							<AccessTypeButton
								value={ShareAccessType.public}>
								<AccessTypeButtonContent
									value={ShareAccessType.public}
									selected={accessType === ShareAccessType.public}
								/>
							</AccessTypeButton>
							<AccessTypeButton value={ShareAccessType.password}>
								<AccessTypeButtonContent
									value={ShareAccessType.password}
									selected={accessType === ShareAccessType.password}
								/>
							</AccessTypeButton>
						</AccessTypeButtonGroup>
					</Box>
					{accessType === ShareAccessType.password && (
						<Box
							sx={(theme) => ({
								// mt: theme.spacing(4),
								p: theme.spacing(4),
								background: '#f8fafc', // TODO
								borderRadius: '10px',
								border: '1px solid #e2e8f0', // TODO
								gap: theme.spacing(2),
								display: 'flex',
								flexDirection: 'column',
							})}
						>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}>
								<Typography component="span" fontSize="14px" fontWeight="bold" color="#0f172a">
									Set {!!id ? 'new' : ''} password
								</Typography>
								<Button
									onClick={() => {
										formik.setFieldValue('password', generatePassword())
									}}
									variant="text"
									sx={{
										textTransform: 'none',
										background: 'none',
										border: 'none',
										color: '#0f172a',
										fontSize: '12px',
										cursor: 'pointer',
										textDecoration: 'underline',
									}}
									disabled={disabled}
								>
									Generate Strong Password
								</Button>
							</Box>
							<FormTextField formik={formik} name="password" fullWidth disabled={disabled}/>
							{/*<Box display="flex" gap={1}>*/}
							{/*	<Box flex="1" height={4} bgcolor={strength > 1 ? '#10b981' : '#e2e8f0'} borderRadius="2px" />*/}
							{/*	<Box flex="1" height={4} bgcolor={strength > 2 ? '#10b981' : '#e2e8f0'} borderRadius="2px" />*/}
							{/*	<Box flex="1" height={4} bgcolor={strength > 3 ? '#10b981' : '#e2e8f0'} borderRadius="2px" />*/}
							{/*	<Box flex="1" height={4} bgcolor={strength > 4 ? '#10b981' : '#e2e8f0'} borderRadius="2px" />*/}
							{/*</Box>*/}
							{/*<Typography>*/}
							{/*	Minimum 8 characters with letters, numbers, and symbols*/}
							{/*</Typography>*/}
						</Box>
					)}
					{type === SHARE_TYPES.project && (
						<>
							<Typography variant="h3" textAlign="left">Permissions</Typography>
							<Box display="flex" flexDirection="column" gap={3}>
								<Box display="flex">
									<Box flex="1">
										<Typography fontWeight="bold">Allow edit project</Typography>
										<Typography fontSize="small">Let viewers edit project data</Typography>
									</Box>
									<SwitchField name="canEdit" formik={formik} disabled={disabled}/>
								</Box>
								<Box display="flex">
									<Box flex="1">
										<Typography fontWeight="bold">Allow add files</Typography>
										<Typography fontSize="small">Let viewers add files to the project</Typography>
									</Box>
									<SwitchField name="canAddFiles" formik={formik} disabled={disabled}/>
								</Box>
								<Box display="flex">
									<Box flex="1">
										<Typography fontWeight="bold">Allow remove files</Typography>
										<Typography fontSize="small">Let viewers remove existing files from the project</Typography>
									</Box>
									<SwitchField name="canRemoveFiles" formik={formik} disabled={disabled}/>
								</Box>
							</Box>
						</>
					)}
				</ShareDialogForm>
			</DialogContent>
			<DialogActions sx={(theme) => ({
				flexWrap: 'wrap-reverse',
				gap: theme.spacing(2),
			})}>
				{!!id && !!enabled && (
					<Button
						onClick={handleDisableShare}
						color="error"
						disabled={disabled}
					>
						Delete share link
					</Button>
				)}
				<Button onClick={handleClose} color="secondary" disabled={disabled}>
					Cancel
				</Button>
				<Button
					onClick={() => handleSubmit(formik.values, formik)}
					color="primary"
					type="submit"
					disabled={disabled || !formik.isValid}
				>
					{!!id ? 'Save changes' : 'Create share link'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
